import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { OfferSource } from '@cds';
import { ParticipantMarketType } from '@cds/betting-offer';
import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { toDictionary } from '@frontend/sports/common/core/utils/collection';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { filterSportsEmitLast } from '@frontend/sports/host-app/sports-product/feature/utils';
import { WinGameFixture } from '@frontend/sports/racing/feature/models';
import { Store } from '@ngrx/store';
import { BetslipType } from 'packages/sports/common/betslip/core/betslip-type';
import { ExternalBetslipActions } from 'packages/sports/common/betslip/core/external-betslip-actions';
import { V1PickId, V2OptionMarketPickId, V2ParticipantPickId, V2WinPickId } from 'packages/sports/common/betslip/core/picks/pick-id';
import { ParticipantPickType } from 'packages/sports/common/betslip/core/picks/pick-models';
import { BetslipIntegrationService } from 'packages/sports/common/betslip/integration/betslip-integration.service';

import { IReBetPicksPayload } from './re-bet.models';

@Injectable()
export class BetslipConnectorService {
    private currentPickCount = 0;
    private betSource = 'rebet';

    constructor(
        private betslipIntegrationService: BetslipIntegrationService,
        private store: Store,
        private betslipConfig: BetslipConfig,
    ) {
        betslipIntegrationService
            .betslipPicksCount$()
            .pipe(filterSportsEmitLast(), takeUntilDestroyed())
            .subscribe((count) => (this.currentPickCount = count));
    }

    addToBetslip(allPicks: IReBetPicksPayload): void {
        for (const p of allPicks.fixtures) {
            const payload = {
                pickId: this.createPickId(p),
                tracking: { source: this.betSource },
            };

            this.betslipIntegrationService.addPick(payload);
        }

        for (const p of allPicks.winGameFixtures) {
            const payload = {
                pickId: this.createWinPickId(p),
                tracking: { source: this.betSource },
            };

            this.betslipIntegrationService.addPick(payload);
        }

        for (const p of allPicks.xCastFixtures) {
            const payload = {
                pickId: this.createXCastPickId(p),
                tracking: { source: this.betSource },
            };

            this.betslipIntegrationService.addPick(payload);
        }

        this.store.dispatch(ExternalBetslipActions.requestBetslipType({ betslipType: BetslipType.Combo }));
    }

    canBeAdded(count: number): boolean {
        return count + this.currentPickCount <= this.betslipConfig.maxBetslipBets;
    }

    private getBetType(pick: WinGameFixture): ParticipantPickType {
        switch (pick.participants[0].options[0].marketType) {
            case ParticipantMarketType.Win:
                return ParticipantPickType.Win;
            case ParticipantMarketType.Forecast:
                return pick.participants.length === 2 ? ParticipantPickType.Forecast : ParticipantPickType.CombinationForecast;
            case ParticipantMarketType.Tricast:
                return pick.participants.length === 3 ? ParticipantPickType.Tricast : ParticipantPickType.CombinationTricast;
        }
    }

    private createXCastPickId(fixture: WinGameFixture): V2ParticipantPickId {
        const participantOrder = toDictionary(
            fixture.participants,
            (p) => p.id,
            (_, index) => index + 1,
        );

        return new V2ParticipantPickId(fixture.id, fixture.participants[0].options[0].marketId, participantOrder, this.getBetType(fixture));
    }

    private createWinPickId(fixture: WinGameFixture): V2WinPickId {
        return new V2WinPickId(fixture.id, fixture.participants[0].options[0].marketId, fixture.participants[0].id);
    }

    private createPickId(event: GridEvent): V1PickId | V2OptionMarketPickId {
        if (event.offerSource === OfferSource.V1) {
            return new V1PickId(event.id, event.optionGroups[0].options[0].optionGroupId, event.optionGroups[0].options[0].id);
        } else {
            const priceId = event.isPriceBoosted ? event.optionGroups[0].options[0].boostedPriceId : event.optionGroups[0].options[0].priceId;

            return new V2OptionMarketPickId(
                event.hybridFixtureData != null ? event.hybridFixtureData.fixtureId : event.id,
                event.optionGroups[0].options[0].optionGroupId,
                event.optionGroups[0].options[0].id,
                priceId!,
            );
        }
    }
}
